import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import { CopyId, Pager, Pill, num, pageInfo, rowsOf, str, useDebounced } from '../ipintel/shared';
import { ExcelButton } from '../techpages/shared';
import { AdminLink } from '../adminNav';
import {
  EXPORT_PATH, type HzCtx, PENDING_EMPTY, type PendingFilters, type Preset, S, applyPreset, publicUrl, uniOptions,
} from './shared';
import HzRunPending from './RunPending';

/**
 * Pending — GET horizon/pending: the technologies with no usable Horizon
 * result under any prompt in the source priority (new universities land
 * here as they are loaded), by university. On top, the card that runs the
 * batch on exactly these from here, with its estimate, cost cap and run log
 * (RunPending.tsx); at the bottom, the same run as server commands
 * (horizon/scripts/horizon_batch_run_v2.py reads the live technologies table
 * and skips what already has a result) — the run commands hidden while a run
 * from the card is going.
 */

const COLUMNS = ['title', 'university_label', 'industry', 'has_summary', 'created_date', 'tech_page_path'];
const LABELS: Record<string, string> = {
  title: 'Technology', university_label: 'University', industry: 'Industry', has_summary: 'Summary',
  created_date: 'Added', tech_page_path: 'Public page',
};

const HzPending: React.FC<{ ctx: HzCtx; preset: Preset<PendingFilters> }> = ({ ctx, preset }) => {
  const last = useRef(preset?.n || 0);
  const [f, setF] = useState<PendingFilters>(() => ({ ...PENDING_EMPTY, ...(preset?.f || {}) }));
  useEffect(() => { applyPreset(preset, PENDING_EMPTY, setF, last); }, [preset]);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const [showCli, setShowCli] = useState(false);
  // A run from the card is going: no command-line run beside it (both would send — and pay for — the same technologies).
  const [runLive, setRunLive] = useState(false);
  const q = useDebounced(f.q);
  const params = useMemo(() => ({ university: f.university, q }), [f.university, q]);
  const key = JSON.stringify(params);
  useEffect(() => { setPage(0); }, [key, perPage]);
  const res = useAdminGet(S, 'pending', { ...params, page, per_page: perPage }, [key, page, perPage, tick, ctx.dataTick]);
  const d = res.data || {};
  const rows = rowsOf(d, 'pending');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, rows, per);
  const byUni = rowsOf(d.by_university).map((u: any) => ({ code: str(u?.code), label: str(u?.label) || str(u?.code), n: num(u?.n), noSummary: num(u?.no_summary) }))
    .filter((u: any) => u.code).sort((a: any, b: any) => b.n - a.n);
  const allPending = byUni.reduce((a: number, u: any) => a + u.n, 0);
  const cmd = d.command || {};
  const runner = d.runner || null;
  const noSummaryAll = byUni.reduce((a: number, u: any) => a + u.noSummary, 0);
  const uni = uniOptions([...ctx.universities, ...byUni.filter((u: any) => !ctx.universities.some(x => x.code === u.code))
    .map((u: any) => ({ code: u.code, label: u.label, name: u.label }))]);

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => setF(s => ({ ...s, q: '' })) });
  if (f.university) chips.push({ label: 'University', value: uni.labels[f.university] || f.university, clear: () => setF(s => ({ ...s, university: '' })) });

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <HzRunPending ctx={ctx} runner={runner} university={f.university} onLive={setRunLive}
        onUniversity={code => setF(s => ({ ...s, university: code }))} />

      {res.error && <Msg kind="error">{res.error}</Msg>}
      <KPIRow>
        <KPI label="Pending technologies" value={(f.university || f.q ? total : allPending || total).toLocaleString()}
          hint={f.university || f.q ? 'matching the filters' : 'no usable Horizon result'} />
        <KPI label="Universities with pending" value={byUni.length.toLocaleString()} />
        {noSummaryAll > 0 && (
          <KPI label="Without a summary" value={noSummaryAll.toLocaleString()} hint="never sent by the runner" />
        )}
      </KPIRow>

      <Section title="Pending technologies"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} technolog{total === 1 ? 'y' : 'ies'}</span>
            {chips.length > 0 && <Btn small onClick={() => setF(PENDING_EMPTY)}>Clear filters</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExcelButton path={EXPORT_PATH} params={{ kind: 'pending', ...params }} onError={setDlErr}
              fallback="horizon-pending" what="These pending technologies" />
          </div>
        }>
        {byUni.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {byUni.map((u: any) => {
              const on = f.university === u.code;
              return (
                <button key={u.code} type="button" onClick={() => setF(s => ({ ...s, university: on ? '' : u.code }))}
                  title={on ? 'Filtering by this — click to clear' : `Only ${u.label}${u.noSummary ? ` (${u.noSummary} without a summary)` : ''}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap ${on
                    ? 'bg-primary-500 border-primary-500 text-grayscale-100 font-bold'
                    : 'bg-white border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                  {u.label}<span className={on ? '' : 'text-amber-700 font-medium'}>{u.n.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={v => setF(s => ({ ...s, q: v }))} wide placeholder="Search title or tech id…" />
          <Select value={f.university} onChange={v => setF(s => ({ ...s, university: v }))} options={uni.options} labels={uni.labels} />
        </div>
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.loading && !res.data ? <Loading label="Finding pending technologies…" /> : (
          <DataTable rows={rows} columns={COLUMNS} labels={LABELS} maxHeight="40rem"
            empty={chips.length ? 'No pending technologies match.' : 'Nothing pending — every technology has Horizon data.'}
            render={{
              title: r => (
                <span className="flex flex-col leading-tight max-w-[24rem]">
                  <button type="button" onClick={() => ctx.openTech(str(r.id))} title="Open this technology"
                    className="text-left font-medium text-grayscale-1300 underline underline-offset-2 truncate">
                    {str(r.title) || str(r.id)}
                  </button>
                  <span className="text-[10px] font-mono text-grayscale-1100 truncate">{str(r.id)}</span>
                </span>
              ),
              university_label: r => <span title={str(r.university_code)}>{str(r.university_label) || str(r.university_code) || '—'}</span>,
              industry: r => <span className="block max-w-[14rem] truncate" title={str(r.industry)}>{str(r.industry) || '—'}</span>,
              has_summary: r => (r.has_summary === false
                ? <span title="The runner never sends a technology without a summary"><Pill tone="bad">none — not sent</Pill></span>
                : r.has_summary === true ? <span className="text-xs text-grayscale-1200">yes</span> : <span className="text-grayscale-1100">—</span>),
              created_date: r => <span className="text-xs">{r.created_date ? fmtDate(r.created_date) : '—'}</span>,
              tech_page_path: r => (r.tech_page_path ? (
                <a href={publicUrl(r.tech_page_path)} target="_blank" rel="noopener noreferrer" title={publicUrl(r.tech_page_path)}
                  className="text-primary-700 underline underline-offset-2 whitespace-nowrap">Open ↗</a>
              ) : <span className="text-xs text-grayscale-1100">no page yet</span>),
            }}
            actions={r => (
              <span className="inline-flex items-center gap-2">
                <Btn small onClick={() => ctx.openTech(str(r.id))}>Details</Btn>
                <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: str(r.id) }}>Tech URL page</AdminLink>
              </span>
            )} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={rows.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>

      <Section title="Or run it from the server's command line"
        right={<Btn small onClick={() => setShowCli(v => !v)}>{showCli ? 'Hide the commands' : 'Show the commands'}</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The same runner as the card above, by hand on the server. It reads the live technologies table and skips every
          technology that already has a result under the final prompt, so a run picks up exactly what is listed here
          (new universities included). The dry run only counts; the real run calls the model.
        </p>
        {showCli && (
          <>
            <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
              A real run makes one Azure OpenAI call per technology and costs money. Do the dry run first and check its
              counts. A command-line run does not refresh the public pages and is not in the run log above: when it
              finishes, press <span className="font-medium">Refresh current data</span> on the Overview so the tech pages
              pick up the new results.
            </div>
            {runner && runner.would_process !== undefined && (
              <div className="text-xs text-grayscale-1200">
                A run would process <span className="font-medium">{num(runner.would_process).toLocaleString()}</span> technologies
                {' '}· ≈ {num(runner.est_tokens).toLocaleString()} tokens
                <span className="text-grayscale-1100"> (cost: see the estimate in the card above — it uses today's prices)</span>
              </div>
            )}
            {runner && (
              <div className="text-xs text-grayscale-1200">
                Prompt the runner would use:{' '}
                {runner.prompt_id ? (
                  <button type="button" className="underline" onClick={() => ctx.goPrompt(str(runner.prompt_id))}>
                    {str(runner.prompt_name) || str(runner.prompt_id).slice(0, 8)}
                  </button>
                ) : <span className="font-medium">a new prompt version (the files match no saved prompt)</span>}
                {runner.prompt_id && (runner.prompt_is_final
                  ? <span className="text-green-700"> — the FINAL prompt ✓</span>
                  : <span className="text-amber-700"> — not the FINAL prompt ⚠ (its results would only fill gaps as a fallback)</span>)}
                {runner.avg_tokens_per_tech ? <span className="text-grayscale-1100"> · ≈ {num(runner.avg_tokens_per_tech).toLocaleString()} tokens per technology</span> : null}
              </div>
            )}
            <div className="flex flex-col gap-2 min-w-0">
              <div className="text-xs font-bold text-grayscale-1200">1. Dry run — counts pending per university, no model calls, no writes</div>
              {cmd.dry_run ? <CopyId value={cmd.dry_run} /> : <span className="text-xs text-grayscale-1100">—</span>}
              <div className="text-xs font-bold text-grayscale-1200">2. Run — processes the pending technologies (model calls, writes results)</div>
              {runLive ? (
                <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
                  Hidden while the run from the card above is going: a command-line run beside it would send — and pay
                  for — the same technologies twice. The command comes back here when that run ends.
                </div>
              ) : cmd.run ? <CopyId value={cmd.run} /> : <span className="text-xs text-grayscale-1100">—</span>}
              {cmd.run_background && !runLive && (
                <>
                  <div className="text-xs font-bold text-grayscale-1200">…or in the background (survives closing the SSH session; log in horizon/logs/)</div>
                  <CopyId value={cmd.run_background} />
                </>
              )}
              {cmd.note && <div className="text-xs text-grayscale-1100 whitespace-pre-wrap break-words">{str(cmd.note)}</div>}
            </div>
          </>
        )}
      </Section>
    </div>
  );
};

export default HzPending;
